import { Footer } from "@/components/footer";
import { PageHeader } from "@/components/page-header";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { aboutPageFaqs, aboutPageStructuredDataJson } from "@/lib/about-page-structured-data";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export const Route = createFileRoute("/_marketing/about-us")({
  head: () => ({
    scripts: [{ type: "application/ld+json", children: aboutPageStructuredDataJson }],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1">
        <PageHeader title="About Veronics" />

        <div className="container mx-auto max-w-4xl px-4 py-12">
          <section aria-labelledby="purpose-heading" className="max-w-3xl">
            <h2 id="purpose-heading" className="text-3xl font-semibold leading-tight text-primary dark:text-foreground">
              Flexible learning communities deserve systems that fit.
            </h2>
            <div className="mt-5 space-y-4 text-base leading-7 text-foreground/85">
              <p>
                Homeschool co-ops, microschools, hybrid programs, and enrichment groups give families more ways to shape
                their children&apos;s education. The people running those communities need systems that support the
                choices they&apos;ve made about how their programs work.
              </p>
              <p>
                Veronics exists to provide that support behind the scenes, helping communities serve families and grow
                while keeping the flexibility that makes them their own.
              </p>
            </div>
          </section>

          <section
            aria-labelledby="story-heading"
            className="mt-12 grid items-start gap-8 border-t border-border pt-12 md:grid-cols-[220px_1fr] md:gap-10"
          >
            <figure className="w-full max-w-[220px]">
              <img
                src="/dane-vanderbilt.jpg"
                alt="Dane Vanderbilt, founder of Veronics"
                width="1080"
                height="1080"
                loading="lazy"
                decoding="async"
                className="aspect-square w-full rounded-lg object-cover"
              />
              <figcaption className="mt-4">
                <p className="font-semibold text-foreground">Dane Vanderbilt</p>
                <p className="mt-1 text-sm text-muted-foreground">Founder of Veronics</p>
                <a
                  href="https://danevanderbilt.com"
                  className="mt-2 inline-flex min-h-11 items-center gap-1 rounded-sm text-sm font-medium text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  More about Dane
                  <ArrowUpRight aria-hidden="true" className="size-4" />
                </a>
              </figcaption>
            </figure>

            <div>
              <h2 id="story-heading" className="text-2xl font-semibold text-primary dark:text-foreground">
                Why I built Veronics
              </h2>
              <div className="mt-5 space-y-4 text-base leading-7 text-foreground/85">
                <p>
                  In 2025, I met with a local learning community about registration. Families emailed teachers when
                  their enrollment window opened, then reported their class choices on a separate form. Administrators
                  had to reconcile the information afterward.
                </p>
                <p>
                  I expected to help improve a few spreadsheets and forms. But as we worked through the process, I saw
                  how much depended on families and teachers keeping track of enrollment rules by hand. The community
                  had put real thought into those rules. It needed software that could support them.
                </p>
                <p>
                  I proposed a dedicated system, and that partnership became Veronics. The platform launched with the
                  community in 2025 and has continued to develop through the questions and feedback that come from using
                  it.
                </p>
              </div>
            </div>
          </section>

          <section aria-labelledby="future-heading" className="mt-12 border-t border-border pt-12">
            <h2 id="future-heading" className="text-2xl font-semibold text-primary dark:text-foreground">
              Where we&apos;re headed
            </h2>
            <div className="mt-5 max-w-3xl space-y-4 text-base leading-7 text-foreground/85">
              <p>
                Our next step is to make Veronics easier for additional communities to adopt, while continuing to
                improve the experience for the people already using it.
              </p>
              <p>
                We plan to strengthen onboarding, administrator tools, and practical guidance for small teams. As we
                work with more groups, we want their experience to guide those improvements and preserve the close
                working relationships that shaped Veronics from the beginning.
              </p>
            </div>
          </section>

          <section aria-labelledby="faq-heading" className="mt-12 border-t border-border pt-12">
            <h2 id="faq-heading" className="text-2xl font-semibold leading-tight text-primary dark:text-foreground">
              Common questions about Veronics
            </h2>
            <Accordion type="single" collapsible className="mt-8">
              {aboutPageFaqs.map(({ question, answer }, index) => (
                <AccordionItem key={question} value={`faq-${index}`} className="border-border">
                  <AccordionTrigger className="text-left text-base font-semibold text-foreground">
                    {question}
                  </AccordionTrigger>
                  <AccordionContent className="max-w-3xl text-base leading-7 text-foreground/80">
                    {answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section aria-labelledby="contact-heading" className="mt-12 pt-8">
            <h2 id="contact-heading" className="text-2xl font-semibold text-primary dark:text-foreground">
              Tell us about your community.
            </h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-foreground/85">
              Let&apos;s talk through how your program runs, what you&apos;d like to make easier, and whether Veronics
              could help.
            </p>
            <Button asChild className="mt-5 min-h-11">
              <Link to="/contact-us">
                Contact us
                <ArrowRight aria-hidden="true" className="ml-2 size-4" />
              </Link>
            </Button>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
